import { Button } from "@hortis/ui/button";
import { DatePicker, createDateLabel } from "@hortis/ui/date-picker";
import { FormControl, FormField, FormItem, FormMessage } from "@hortis/ui/form";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import { IntersectCircle, Map2, Tag1, Tag3 } from "@hortis/ui/icons";
import { CalendarMinus2 } from "@hortis/ui/icons/calendar-minus-2";
import { CalendarPlus2 } from "@hortis/ui/icons/calendar-plus-2";
import { RichTextEditor } from "@hortis/ui/rich-text-editor";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { useRouteContext } from "@tanstack/react-router";
import { MaterialGroup, PlantMaterialStatus } from "generated/graphql";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  MaterialTypePicker,
  MaterialTypePickerLabel,
} from "src/components/pickers/material-type-picker";
import {
  StatusPicker,
  StatusPickerLabel,
} from "src/components/pickers/status-picker";
import { TagMultiPicker } from "src/components/pickers/tag-picker/tag-picker";
import { mention } from "src/components/rich-text-editor/mention";
import { LocationPicker } from "src/features/locations/components/location-picker";
import { useAccessTokenBasic } from "src/utils/use-access-token";
import { twMerge } from "tailwind-merge";
import { MaterialSchema } from "../add-plant-material-form/schema";
import { StagedImage } from "./add-material-photos-button";
import { MaterialFieldsMoreDropdown } from "./material-fields-more-dropdown";
import { MaterialPhotos } from "./material-photos";

export const materialDefaultValues = {
  firstPresent: new Date(),
  materialGroup: MaterialGroup.Plant,
  status: PlantMaterialStatus.Present,
  massPlanting: false,
  notesData: undefined,
  tags: [],
  images: [],
};

export const MaterialNestedFields = ({
  siteSlug,
  form,
  stagedPhotos,
  setStagedPhotos,
}: {
  form: UseFormReturn<MaterialSchema>;
  siteSlug: string;
  stagedPhotos: Array<StagedImage>;
  setStagedPhotos: Dispatch<SetStateAction<Array<StagedImage>>>;
}) => {
  const { subdomain } = useRouteContext({ from: "__root__" });
  const accessToken = useAccessTokenBasic();
  const isDesktop = useMediaQuery("md");

  const materialGroup = form.watch("materialGroup");

  useEffect(() => {
    if (materialGroup === MaterialGroup.Seed) {
      form.setValue("massPlanting", false);
    }
  }, [materialGroup, form]);

  const statusOption = form.watch("status");
  const hideDateRemovedField = useMemo(() => {
    return statusOption !== PlantMaterialStatus.Absent;
  }, [statusOption]);

  useEffect(() => {
    if (hideDateRemovedField) {
      form.setValue("firstAbsent", null);
    } else if (form.getValues("firstAbsent") == null) {
      form.setValue("firstAbsent", new Date());
    }
  }, [hideDateRemovedField, form]);

  const notesInputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (isDesktop) {
      notesInputRef.current?.focus();
    }
  }, [isDesktop]);

  return (
    <div className="space-y-4 px-4 pb-4 pt-4">
      <FormField
        control={form.control}
        name="notesData"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              {accessToken != null && (
                <RichTextEditor
                  value={field.value}
                  onChange={field.onChange}
                  data-cy="material-notes-input"
                  placeholder="Add notes..."
                  extensions={[mention({ accessToken, siteSlug, subdomain })]}
                  className="rounded-none border-none shadow-none focus-within:ring-0 [&_.richTextEditor]:min-h-16 [&_.richTextEditor]:px-0 [&_.richTextEditor]:py-0 bg-transparent"
                />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="flex flex-wrap gap-2">
        <FormField
          control={form.control}
          name="firstPresent"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Tooltip>
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    data-cy="date-added-picker"
                  >
                    <TooltipTrigger asChild>
                      <Button
                        startIcon={CalendarPlus2}
                        size="xs"
                        className={twMerge(
                          // eslint-disable-next-line sonarjs/no-duplicate-string
                          field.value == null && "text-grey-500",
                        )}
                      >
                        {field.value == null
                          ? "Date added"
                          : createDateLabel(field.value)}
                      </Button>
                    </TooltipTrigger>
                  </DatePicker>
                  <TooltipContent side="bottom" sideOffset={0}>
                    Change first present
                  </TooltipContent>
                </Tooltip>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="materialGroup"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Tooltip>
                  <MaterialTypePicker
                    placeholder={
                      field.value == null
                        ? "Set material type..."
                        : "Change material type..."
                    }
                    {...field}
                  >
                    <TooltipTrigger asChild>
                      <Button
                        size="xs"
                        startIcon={Tag1}
                        className={twMerge(
                          field.value == null && "text-grey-500",
                        )}
                      >
                        {field.value == null ? (
                          "Type"
                        ) : (
                          <MaterialTypePickerLabel type={field.value} />
                        )}
                      </Button>
                    </TooltipTrigger>
                  </MaterialTypePicker>
                  <TooltipContent side="bottom" sideOffset={0}>
                    Change material type
                  </TooltipContent>
                </Tooltip>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Tooltip>
                  <StatusPicker
                    placeholder={
                      field.value == null ? "Set status..." : "Change status..."
                    }
                    {...field}
                  >
                    <TooltipTrigger asChild>
                      <Button
                        startIcon={
                          field.value == null ? IntersectCircle : undefined
                        }
                        size="xs"
                        className={twMerge(
                          field.value == null && "text-grey-500",
                        )}
                      >
                        {field.value == null ? (
                          "Status"
                        ) : (
                          <StatusPickerLabel value={field.value} />
                        )}
                      </Button>
                    </TooltipTrigger>
                  </StatusPicker>
                  <TooltipContent side="bottom" sideOffset={0}>
                    Change status
                  </TooltipContent>
                </Tooltip>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {!hideDateRemovedField && (
          <FormField
            control={form.control}
            name="firstAbsent"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Tooltip>
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      data-cy="date-removed-picker"
                    >
                      <TooltipTrigger asChild>
                        <Button
                          startIcon={CalendarMinus2}
                          size="xs"
                          className={twMerge(
                            field.value == null && "text-grey-500",
                          )}
                        >
                          {field.value == null
                            ? "Date removed"
                            : createDateLabel(field.value)}
                        </Button>
                      </TooltipTrigger>
                    </DatePicker>
                    <TooltipContent side="bottom" sideOffset={0}>
                      Change date removed
                    </TooltipContent>
                  </Tooltip>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="collectionSiteLocationOption"
          render={({ field, fieldState }) => (
            <FormItem>
              <Tooltip>
                <LocationPicker
                  value={field.value}
                  onChange={field.onChange}
                  data-cy="location-picker"
                  placeholder={
                    field.value == null
                      ? "Set location..."
                      : "Change location..."
                  }
                >
                  <TooltipTrigger asChild>
                    <FormControl>
                      <Button
                        startIcon={Map2}
                        size="xs"
                        className={twMerge(
                          "max-w-48",
                          field.value == null && "text-grey-500",
                          fieldState.error &&
                            "border-error-500 text-error-700 hover:border-error-500 hover:bg-error-25 hover:text-error-700",
                        )}
                      >
                        {field.value == null ? (
                          <div>
                            Location
                            <span className="ml-0.5 text-orange-500">*</span>
                          </div>
                        ) : (
                          field.value.name
                        )}
                      </Button>
                    </FormControl>
                  </TooltipTrigger>
                </LocationPicker>
                <TooltipContent side="bottom" sideOffset={0}>
                  {field.value == null ? "Set location" : "Change location"}
                </TooltipContent>
              </Tooltip>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="tags"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Tooltip>
                  <TagMultiPicker
                    data-cy="material-tags-picker"
                    placeholder={
                      field.value == null || field.value.length === 0
                        ? "Add tags..."
                        : "Change tags..."
                    }
                    tagLevel="site"
                    newTagOption
                    {...field}
                  >
                    <TooltipTrigger asChild>
                      <Button
                        startIcon={Tag3}
                        size="xs"
                        className={twMerge(
                          "max-w-48",
                          (field.value == null || field.value.length === 0) &&
                            "text-grey-500",
                        )}
                      >
                        {field.value == null || field.value.length === 0 ? (
                          "Tags"
                        ) : (
                          <div className="truncate">
                            {field.value.length === 1
                              ? field.value[0]?.name
                              : `${field.value.length} tags`}
                          </div>
                        )}
                      </Button>
                    </TooltipTrigger>
                  </TagMultiPicker>
                  <TooltipContent side="bottom" sideOffset={0}>
                    {field.value == null || field.value.length === 0
                      ? "Add tags"
                      : "Change tags"}
                  </TooltipContent>
                </Tooltip>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <MaterialFieldsMoreDropdown siteSlug={siteSlug} />
      </div>
      <MaterialPhotos
        images={stagedPhotos}
        deletePhoto={(id) => {
          setStagedPhotos((images) => images.filter((i) => i.id !== id));
        }}
        onPhotoSave={(id, newPhoto) => {
          setStagedPhotos((images) =>
            images.map((image) => (image.id === id ? newPhoto : image)),
          );
        }}
      />
    </div>
  );
};
